import { format } from 'svelte-i18n';
import { get } from 'svelte/store';

import { navigateToDevice, navigateToSetting } from 'src/stores/ui/navigate-to';
import { capFirstChar } from 'src/helpers/utilities';
import { DeviceNotificationBuilder } from 'src/helpers/notifications/DeviceNotificationBuilder';
import type { NotificationType } from 'src/helpers/notifications/DeviceNotificationBuilder';
import type { DeviceNotification } from 'src/helpers/notifications/DeviceNotification';
import type { Device } from 'src/types/Device';
import { getCameraComponentKey } from './camera-helpers';
import { getStorageComponentKey } from './camera-storage-helpers';
import { CAMERA_ERROR, getStorageError, STORAGE_ERROR } from '../types/camera-errors';
import { CAMERA_ERROR_LABELS, STORAGE_STATE_LABELS } from './camera-errors';

const _format = get(format);

/** Status component: `camera:X` or `storage:0`. */
type CameraNotificationSource = 'camera' | 'storage';

/**
 * Maps a device status error to a built device notification.
 * Registry keys must match error strings from device status.
 */
type CameraDeviceNotificationConfig = {
	/** Badge and panel severity. */
	type: NotificationType;
	/** Status component to read errors from (`camera` or `storage`). */
	source: CameraNotificationSource;
	/** Drawer heading label (`heading.local`). */
	localLabel: { key: string; default: string };
	/** Global panel heading i18n key (`{deviceName}` supported). */
	globalHeadingKey: string;
	globalHeadingDefault: string;
	/** Notification body i18n key. */
	messageKey: string;
	messageDefault: string;
	/** Settings section for local CTA (e.g. `settings`, `storage`). */
	settingsSection: string;
	/** Settings sub-target for local CTA (e.g. `firmware_version`, `local`). */
	settingsSubSetting: string;
	/** Local CTA button i18n key. */
	settingsCtaTitleKey: string;
	settingsCtaTitleDefault: string;
};

/**
 * Device-notification definitions for camera component and storage errors.
 * Keys match the error strings reported in device status.
 */
export const CAMERA_DEVICE_NOTIFICATIONS: Record<string, CameraDeviceNotificationConfig> = {
	[CAMERA_ERROR.STREAMER_FS_BAD]: {
		type: 'danger',
		source: 'camera',
		localLabel: CAMERA_ERROR_LABELS[CAMERA_ERROR.STREAMER_FS_BAD],
		globalHeadingKey: 'notification_global_camera_streamer_fs_bad',
		globalHeadingDefault: '{deviceName} — streamer update required',
		messageKey: 'notification_message_camera_streamer_fs_bad',
		messageDefault: 'Update the camera streamer firmware to restore streaming.',
		settingsSection: 'settings',
		settingsSubSetting: 'firmware_version',
		settingsCtaTitleKey: 'sub_settings_title_update_firmware',
		settingsCtaTitleDefault: 'Update firmware',
	},
	[STORAGE_ERROR.NEEDS_FORMATTING]: {
		type: 'warning',
		source: 'storage',
		localLabel: STORAGE_STATE_LABELS[STORAGE_ERROR.NEEDS_FORMATTING],
		globalHeadingKey: 'notification_global_storage_needs_format',
		globalHeadingDefault: '{deviceName} — storage needs formatting',
		messageKey: 'notification_message_storage_needs_format',
		messageDefault: 'Format the storage to enable local recordings.',
		settingsSection: 'storage',
		settingsSubSetting: 'local',
		settingsCtaTitleKey: 'navigate_to_storage_settings',
		settingsCtaTitleDefault: 'Go to storage settings',
	},
	[STORAGE_ERROR.MOUNT_FAILED]: {
		type: 'danger',
		source: 'storage',
		localLabel: STORAGE_STATE_LABELS[STORAGE_ERROR.MOUNT_FAILED],
		globalHeadingKey: 'notification_global_storage_mount_failed',
		globalHeadingDefault: '{deviceName} — storage mount failed',
		messageKey: 'notification_message_storage_mount_failed',
		messageDefault: 'Check the storage device and storage settings.',
		settingsSection: 'storage',
		settingsSubSetting: 'local',
		settingsCtaTitleKey: 'navigate_to_storage_settings',
		settingsCtaTitleDefault: 'Go to storage settings',
	},
};

/**
 * Compose the `setError` value used for the badge i18n key
 * `text_device_{errorKey}_notification_option`. Errors are namespaced with their
 * source (`camera_` / `storage_`) to avoid clashing across components.
 */
const composeErrorKey = (source: CameraNotificationSource, error: string): string =>
	`${source}_${error}`;

const getComponentErrors = (
	source: CameraNotificationSource,
	status: Record<string, any> | undefined,
	device: Device,
): string[] | undefined => {
	const key = source === 'camera' ? getCameraComponentKey(device) : getStorageComponentKey(device);
	return status?.[key]?.errors;
};

/**
 * Collect active camera/storage device notifications from device status.
 */
export const collectCameraDeviceNotificationConditions = (
	device: Device,
	status: Record<string, any> | undefined,
): Record<string, boolean> => {
	const notifications: Record<string, boolean> = {};

	if (!status?.online) {
		return notifications;
	}

	for (const [error, config] of Object.entries(CAMERA_DEVICE_NOTIFICATIONS)) {
		const errors = getComponentErrors(config.source, status, device);
		if (errors?.some((reported) => getStorageError(reported) === error)) {
			notifications[error] = true;
		}
	}

	return notifications;
};

/**
 * Build a device notification for a known camera/storage error, if supported.
 */
export const buildCameraDeviceNotification = (
	notification: string,
	device: Device,
): DeviceNotification | undefined => {
	const config = CAMERA_DEVICE_NOTIFICATIONS[notification];
	if (!config) {
		return undefined;
	}

	return new DeviceNotificationBuilder(`${device.id}-${notification}`, config.type)
		.setHeading({
			global: _format(config.globalHeadingKey, {
				default: config.globalHeadingDefault,
				values: { deviceName: device.name },
			}),
			local: _format(config.localLabel.key, { default: config.localLabel.default }),
		})
		.setMessage(
			_format(config.messageKey, {
				default: config.messageDefault,
			}),
		)
		.setCtaGlobal({
			title: capFirstChar(_format('navigate_to_device', { default: 'Go to device' })),
			onClick: () => navigateToDevice(device.id),
		})
		.setCtaLocal({
			title: capFirstChar(
				_format(config.settingsCtaTitleKey, { default: config.settingsCtaTitleDefault }),
			),
			onClick: () =>
				navigateToSetting(device.id, config.settingsSection, config.settingsSubSetting),
		})
		.setError(composeErrorKey(config.source, notification))
		.build();
};
